/** 原始输入屏「数据存储层运维动作」（dual-entry E6，#5009；raw-data.md「危险动作」节）。
 *
 * 五组按钮：立即同步、重新核对、立即落库、立即迁入（回看 N 天）、清理预演 / 执行。
 * 写入口一律带操作人与理由走 `post()`（带令牌），受理后轮询 `GET /jobs/{job_id}` 到终态；
 * 未配库 / 未配镜像时后端 501「写入口未接数据源」，这里原样显示 detail，不当成功。
 */

import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { post, query } from "../../api/client";
import { isRetryable } from "../../api/errors";
import { notProvided } from "../../api/g4";
import { Card, Note } from "../../components/Card";
import { ErrorState } from "../../components/States";
import { Table } from "../../components/Table";
import {
  parsePrunePreview,
  STORE_ACTIONS,
  storeActionBody,
  type PrunePreview,
  type StoreAction,
  type StoreActionDef,
  type StoreActionInput,
} from "../../lib/storeOps";

const READ = { retry: (count: number, error: unknown) => count < 2 && isRetryable(error), retryDelay: 1_500 } as const;

export function StoreActions(): ReactNode {
  return (
    <Card title="数据存储层运维动作" sub="后台任务 · 须操作人与理由 · 受理即留痕（events.jsonl kind=store + SSE store）">
      <div className="grid g2">
        {STORE_ACTIONS.filter((a) => a.action !== "prune").map((def) => (
          <ActionCard key={def.action} def={def} />
        ))}
        <PruneCard def={STORE_ACTIONS.find((a) => a.action === "prune")!} />
      </div>
    </Card>
  );
}

function errorText(error: unknown): string {
  const gap = notProvided(error);
  if (gap) return `接口未提供：${gap.detail}`;
  return error instanceof Error ? error.message : String(error);
}

function useStoreMutation(path: string, onJob: (jobId: string | null) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => post<Record<string, unknown>>(path, body),
    onSuccess: (result) => {
      onJob(typeof result["job_id"] === "string" ? result["job_id"] : null);
      void client.invalidateQueries({ queryKey: ["g4"] });
    },
  });
}

function ActorFields({ input, update }: { input: StoreActionInput; update: (patch: Partial<StoreActionInput>) => void }): ReactNode {
  return (
    <>
      <label className="f">
        <span>操作人</span>
        <input value={input.operator} onChange={(e) => update({ operator: e.target.value })} />
      </label>
      <label className="f">
        <span>理由</span>
        <input value={input.reason} onChange={(e) => update({ reason: e.target.value })} />
      </label>
    </>
  );
}

function ActionCard({ def }: { def: StoreActionDef }): ReactNode {
  const [input, setInput] = useState<StoreActionInput>({ operator: "", reason: "", lookback_days: "7" });
  const [jobId, setJobId] = useState<string | null>(null);
  const mutation = useStoreMutation(def.path, setJobId);
  const built = storeActionBody(def.action, input);
  const update = (patch: Partial<StoreActionInput>) => setInput((v) => ({ ...v, ...patch }));
  const title = def.action === "sync" ? `立即迁入（回看 ${input.lookback_days || "7"} 天）` : def.title;
  return (
    <Card title={title} sub={`POST ${def.path}`}>
      <div className="hint">{def.hint}</div>
      <div className="fieldrow">
        {def.action === "mirror-sync" ? (
          <label className="f">
            <span>交易日（可空）</span>
            <input type="date" value={input.trade_date ?? ""} onChange={(e) => update({ trade_date: e.target.value })} />
          </label>
        ) : null}
        {def.action === "mirror-sync" || def.action === "sync" ? (
          <label className="f">
            <span>回看天数</span>
            <input type="number" min={1} value={input.lookback_days ?? ""} onChange={(e) => update({ lookback_days: e.target.value })} />
          </label>
        ) : null}
        <ActorFields input={input} update={update} />
        {def.action === "mirror-sync" ? <Check label="演练（只统计）" value={input.dry_run} set={(v) => update({ dry_run: v })} /> : null}
        {def.action === "verify" ? <Check label="含镜像行数" value={input.mirror} set={(v) => update({ mirror: v })} /> : null}
        {def.action === "ingest" ? <Check label="强制重落" value={input.force} set={(v) => update({ force: v })} /> : null}
      </div>
      <button
        type="button"
        className="btn primary"
        disabled={"error" in built || mutation.isPending}
        onClick={() => {
          if ("body" in built) mutation.mutate(built.body);
        }}
      >
        {mutation.isPending ? "提交中…" : title}
      </button>
      {"error" in built ? <Note tone="warn">{built.error}</Note> : null}
      {mutation.isError ? <Note tone="bad">{errorText(mutation.error)}</Note> : null}
      {jobId ? <JobProgress jobId={jobId} /> : null}
    </Card>
  );
}

function Check({ label, value, set }: { label: string; value: boolean | undefined; set: (v: boolean) => void }): ReactNode {
  return (
    <label className="f check">
      <span>{label}</span>
      <input type="checkbox" checked={value === true} onChange={(e) => set(e.target.checked)} />
    </label>
  );
}

function PruneCard({ def }: { def: StoreActionDef }): ReactNode {
  const [input, setInput] = useState<StoreActionInput>({ operator: "", reason: "" });
  const [jobId, setJobId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const preview = useQuery<PrunePreview, Error>({
    queryKey: ["g4", "store-prune-preview"],
    queryFn: async () => parsePrunePreview(await query<unknown>("/store/prune-preview")),
    enabled: previewing,
    ...READ,
  });
  const mutation = useStoreMutation(def.path, setJobId);
  const built = storeActionBody("prune" satisfies StoreAction, input);
  const update = (patch: Partial<StoreActionInput>) => setInput((v) => ({ ...v, ...patch }));
  const plan = preview.data;
  return (
    <Card title="清理预演 / 执行" sub="GET /store/prune-preview · POST /store/prune">
      <div className="hint">{def.hint}</div>
      <button type="button" className="btn" onClick={() => (previewing ? void preview.refetch() : setPreviewing(true))}>
        清理预演
      </button>
      {previewing && preview.status === "error" ? <Note tone="warn">{errorText(preview.error)}</Note> : null}
      {plan ? (
        plan.total === 0 ? (
          <Note>预演：没有可清理的投影（文件侧的策略日都还在）。</Note>
        ) : (
          <Table
            cols={[{ label: "策略" }, { label: "文件侧已不存在的日子", wrap: true }, { label: "待删行数", num: true }]}
            rows={plan.strategies.map((s) => ({
              key: s.strategy_id,
              cells: [s.strategy_id, s.missing_days.join("、") || "—", Object.values(s.tables).reduce((a, b) => a + b, 0)],
            }))}
            empty="预演没有策略"
          />
        )
      ) : null}
      <div className="fieldrow">
        <ActorFields input={input} update={update} />
      </div>
      <button
        type="button"
        className="btn danger"
        disabled={"error" in built || mutation.isPending || !plan || plan.total === 0}
        onClick={() => {
          if (!("body" in built) || !plan) return;
          if (!window.confirm(`确认清理执行？将在单事务中删除 ${plan.total} 行投影（预演结果）`)) return;
          mutation.mutate(built.body);
        }}
      >
        {mutation.isPending ? "提交中…" : "清理执行"}
      </button>
      {!plan ? <Note tone="warn">先做清理预演，看清要删什么再执行。</Note> : "error" in built ? <Note tone="warn">{built.error}</Note> : null}
      {mutation.isError ? <Note tone="bad">{errorText(mutation.error)}</Note> : null}
      {jobId ? <JobProgress jobId={jobId} /> : null}
    </Card>
  );
}

function JobProgress({ jobId }: { jobId: string }): ReactNode {
  const client = useQueryClient();
  const q = useQuery<Record<string, unknown>, Error>({
    queryKey: ["jobs", jobId],
    queryFn: () => query<Record<string, unknown>>(`/jobs/${encodeURIComponent(jobId)}`),
    refetchInterval: (state) => (state.state.data?.["status"] === "running" ? 1_500 : false),
    ...READ,
  });
  const status = q.data?.["status"];
  useEffect(() => {
    if (status === "ok" || status === "failed") void client.invalidateQueries({ queryKey: ["g4"] });
  }, [status, client]);
  if (q.status === "error") return <ErrorState error={q.error} what={`任务 ${jobId}`} />;
  return (
    <Note tone={status === "failed" ? "bad" : ""}>
      任务 <code>{jobId}</code>：{status === undefined ? "查询中" : String(status)}
      {status === "failed" ? `；${String(q.data?.["error"] ?? "")}` : ""}
    </Note>
  );
}
